<template>
    <div
        class="global_view_content lesson-view"
        :class="{ 'lesson-view--dark': Boolean(dark) }"
    >
        <div class="lesson-view__inner">
            <!-- Cabeçalho contextual -->
            <header
                v-if="courseName || currentModuleTitle"
                class="lesson-context"
            >
                <nav class="lesson-context__breadcrumb" :aria-label="$t('student.player.courseContext')">
                    <span
                        v-if="courseName"
                        class="lesson-context__crumb"
                        :title="courseName"
                    >
                        {{ courseName }}
                    </span>
                    <template v-if="currentModuleTitle">
                        <span class="lesson-context__sep" aria-hidden="true">/</span>
                        <span
                            class="lesson-context__crumb lesson-context__crumb--current"
                            :title="currentModuleTitle"
                        >
                            {{ currentModuleTitle }}
                        </span>
                    </template>
                </nav>
                <p
                    v-if="positionLabel"
                    class="lesson-context__position"
                >
                    {{ positionLabel }}
                </p>
            </header>

            <!-- Sem aulas -->
            <div
                v-if="hasNoLessons"
                class="lesson-state"
            >
                <v-icon size="36" color="#98A2B3">mdi-book-open-blank-variant</v-icon>
                <h2 class="lesson-state__title">{{ $t('student.player.contentUnavailable') }}</h2>
                <p class="lesson-state__text">{{ $t('student.player.contactAdmin') }}</p>
            </div>

            <!-- Loading -->
            <div
                v-else-if="lessonLoading"
                class="lesson-loading"
                aria-busy="true"
                :aria-label="$t('student.player.loadingLesson')"
            >
                <div class="lesson-loading__player">
                    <v-skeleton-loader type="image" height="100%" />
                </div>
                <v-skeleton-loader type="heading, sentences" class="lesson-loading__text" />
            </div>

            <!-- Erro -->
            <div
                v-else-if="lessonError"
                class="lesson-state"
                role="alert"
            >
                <v-icon size="36" color="#D92D20">mdi-alert-circle-outline</v-icon>
                <h2 class="lesson-state__title">{{ $t(enrollmentDenied ? 'student.player.noEnrollment' : 'student.player.loadError') }}</h2>
                <p class="lesson-state__text">{{ $t(enrollmentDenied ? 'student.player.noEnrollmentHint' : 'student.player.loadErrorHint') }}</p>
                <v-btn
                    color="primary"
                    variant="outlined"
                    @click="retryLoadLesson"
                >
                    {{ $t('common.retry') }}
                </v-btn>
            </div>

            <!-- Fim do curso -->
            <div
                v-else-if="showCourseFinished"
                class="lesson-state lesson-state--success"
            >
                <v-icon size="40" color="#039855">mdi-check-circle-outline</v-icon>
                <h2 class="lesson-state__title">{{ $t('student.player.courseCompleted') }}</h2>
                <p class="lesson-state__text">
                    {{ $t('student.player.courseCompletedHint') }}
                </p>
                <v-btn
                    color="primary"
                    @click="goToCourses"
                >
                    {{ $t('student.player.backToCourses') }}
                </v-btn>
            </div>

            <template v-else>
                <!-- YouTube -->
                <div class="lesson-media" v-show="Number(typeLessonPortal) === 2">
                    <div class="lesson-media__aspect" v-if="videoLink">
                        <iframe
                            :key="videoLink"
                            :src="`https://www.youtube.com/embed/${videoLink}`"
                            title="YouTube video player"
                            frameborder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            referrerpolicy="strict-origin-when-cross-origin"
                            allowfullscreen
                        />
                    </div>
                    <div v-else class="lesson-pdf__empty">{{ $t('student.player.contentUnavailable') }}</div>
                </div>

                <!-- Vimeo -->
                <div class="lesson-media" v-show="Number(typeLessonPortal) === 3">
                    <div class="lesson-media__aspect" v-if="videoLink">
                        <iframe
                            :key="videoLink"
                            :src="`https://player.vimeo.com/video/${videoLink}`"
                            frameborder="0"
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowfullscreen
                        />
                    </div>
                    <div v-else class="lesson-pdf__empty">{{ $t('student.player.contentUnavailable') }}</div>
                </div>

                <!-- Questionário -->
                <div class="lesson-quiz" v-if="Number(typeLessonPortal) === 5" :key="typeLessonPortal">
                    <div
                        v-show="hasReadyQuizScore"
                        class="lesson-quiz__summary"
                        :aria-label="$t('student.player.quizResult')"
                    >
                        <div class="lesson-quiz__stat lesson-quiz__stat--grade">
                            <p class="lesson-quiz__stat-label">{{ $t('student.player.yourGrade') }}</p>
                            <p class="lesson-quiz__stat-value lesson-quiz__stat-value--xl">
                                <template v-if="hasReadyQuizScore">
                                    {{ scoreData.grade ? Math.round(scoreData.grade / 10) : 0 }}
                                </template>
                                <template v-else>
                                    {{ returnAnswer ? Math.round(returnAnswer.grade / 10) : 0 }}
                                </template>
                            </p>
                        </div>
                        <div class="lesson-quiz__stat lesson-quiz__stat--hits">
                            <p class="lesson-quiz__stat-label">{{ $t('student.player.correctAnswers') }}</p>
                            <p class="lesson-quiz__stat-value">
                                <i class="pi pi-check lesson-quiz__stat-icon" aria-hidden="true"></i>
                                <template v-if="hasReadyQuizScore">
                                    {{ scoreData.acertos }}
                                </template>
                                <template v-else>
                                    {{ returnAnswer ? returnAnswer.acertos : 0 }}
                                </template>
                            </p>
                        </div>
                        <div class="lesson-quiz__stat lesson-quiz__stat--errors">
                            <p class="lesson-quiz__stat-label">{{ $t('student.player.wrongAnswers') }}</p>
                            <p class="lesson-quiz__stat-value">
                                <i class="pi pi-times lesson-quiz__stat-icon" aria-hidden="true"></i>
                                <template v-if="hasReadyQuizScore">
                                    {{ scoreData.erros }}
                                </template>
                                <template v-else>
                                    {{ returnAnswer ? returnAnswer.erros : 0 }}
                                </template>
                            </p>
                        </div>
                    </div>

                    <div class="quiz-content">
                        <Question
                            :review="review"
                            :dataQuiz="dataQuiz"
                            :userAmswer="userAmswer"
                            @quiz-submitted="handleQuizSubmitted"
                        />
                    </div>
                </div>

                <!-- PDF -->
                <div class="lesson-pdf" v-show="Number(typeLessonPortal) === 4" :key="typeLessonPortal">
                    <template v-if="pdfSource">
                        <iframe
                            v-if="isMobile"
                            class="lesson-pdf__frame"
                            :src="`https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(pdfSource)}`"
                            frameborder="0"
                            allowfullscreen
                        />
                        <embed v-else class="lesson-pdf__embed" :src="pdfSource" />
                    </template>
                    <div
                        v-else
                        class="lesson-pdf__empty"
                    >
                        {{ $t('student.player.pdfLoadError') }}
                    </div>
                </div>

                <!-- Detalhes da aula + navegação -->
                <section
                    v-if="showLessonDetails"
                    class="lesson-details"
                    :aria-labelledby="showLessonAbout ? 'lesson-about-title' : undefined"
                >
                    <div
                        v-if="showLessonAbout"
                        class="lesson-details__header"
                    >
                        <div class="lesson-details__main">
                            <p
                                v-if="lessonMetaParts.length"
                                class="lesson-details__meta"
                            >
                                <template
                                    v-for="(part, idx) in lessonMetaParts"
                                    :key="idx"
                                >
                                    <span
                                        v-if="idx > 0"
                                        class="lesson-details__meta-sep"
                                        aria-hidden="true"
                                    >•</span>
                                    <span>{{ part }}</span>
                                </template>
                            </p>

                            <h1
                                id="lesson-about-title"
                                class="lesson-details__title"
                            >
                                {{ lessonDetail.title }}
                            </h1>

                            <p
                                v-if="lessonDescriptionPlain"
                                class="lesson-details__description"
                            >
                                {{ lessonDescriptionPlain }}
                            </p>
                        </div>

                        <span
                            v-if="isLessonCompleted"
                            class="lesson-details__status"
                        >
                            <v-icon size="16" color="#039855">mdi-check-circle-outline</v-icon>
                            {{ $t('student.player.lessonCompleted') }}
                        </span>
                    </div>

                    <!-- Videoteca (entre descrição e navegação) -->
                    <div
                        v-if="Number(typeLessonPortal) === 1"
                        class="lesson-media lesson-media--in-details"
                        :key="`videoteca-${typeLessonPortal}`"
                    >
                        <div
                            v-if="playerError"
                            class="lesson-pdf__empty"
                        >
                            {{ $t('student.player.playerLoadError') }}
                        </div>
                        <div
                            v-else
                            class="lesson-media__aspect"
                            :aria-busy="playerLoading ? 'true' : 'false'"
                            :aria-label="playerLoading ? $t('student.player.loadingVideo') : undefined"
                        >
                            <div
                                v-if="playerLoading"
                                class="lesson-media__loader"
                            >
                                <v-progress-circular
                                    indeterminate
                                    color="primary"
                                    size="48"
                                    width="4"
                                />
                                <span class="lesson-media__loader-text">
                                    {{ $t('student.player.loadingVideo') }}
                                </span>
                            </div>
                            <iframe
                                v-if="videoPlayer"
                                :key="videoPlayer"
                                class="lesson-media__iframe"
                                :class="{ 'lesson-media__iframe--ready': !playerLoading }"
                                :src="videoPlayer"
                                frameborder="0"
                                allow="autoplay; fullscreen"
                                allowfullscreen
                                @load="onPlayerIframeLoad"
                            />
                        </div>
                    </div>

                    <nav
                        v-if="showLessonNav"
                        class="lesson-details__navigation"
                        :class="{ 'lesson-details__navigation--alone': !showLessonAbout }"
                        :aria-label="$t('student.player.lessonNav')"
                    >
                        <div class="lesson-details__previous">
                            <v-btn
                                variant="outlined"
                                color="primary"
                                class="lesson-details__btn lesson-details__btn--prev"
                                :disabled="!hasPreviousLesson || completing"
                                :aria-label="previousLessonAriaLabel"
                                @click="goToPreviousLesson"
                            >
                                <v-icon start size="18">mdi-chevron-left</v-icon>
                                {{ $t('student.player.previousLesson') }}
                            </v-btn>
                            <span
                                v-if="hasPreviousLesson && previousLessonTitle"
                                class="lesson-details__hint"
                            >
                                {{ previousLessonTitle }}
                            </span>
                        </div>

                        <div class="lesson-details__next">
                            <v-btn
                                v-if="showCompleteAction"
                                color="primary"
                                class="lesson-details__btn lesson-details__btn--main"
                                :loading="completing"
                                :disabled="completing"
                                :aria-label="primaryActionLabel"
                                @click="completeAndContinue"
                            >
                                {{ primaryActionLabel }}
                                <v-icon
                                    v-if="hasNextLesson"
                                    end
                                    size="18"
                                >
                                    mdi-chevron-right
                                </v-icon>
                            </v-btn>
                            <v-btn
                                v-else-if="hasNextLesson"
                                color="primary"
                                class="lesson-details__btn lesson-details__btn--main"
                                :disabled="completing"
                                :aria-label="nextLessonAriaLabel"
                                :title="nextLessonTitle || undefined"
                                @click="goToNextLesson"
                            >
                                {{ $t('student.player.nextLesson') }}
                                <v-icon end size="18">mdi-chevron-right</v-icon>
                            </v-btn>
                            <v-btn
                                v-else-if="isLessonCompleted || Number(typeLessonPortal) === 5"
                                color="primary"
                                class="lesson-details__btn lesson-details__btn--main"
                                @click="goToCourses"
                            >
                                {{ $t('student.player.backToCourses') }}
                            </v-btn>

                            <span
                                v-if="hasNextLesson && nextLessonTitle"
                                class="lesson-details__hint"
                            >
                                {{ nextLessonTitle }}
                            </span>
                        </div>
                    </nav>
                </section>
            </template>
        </div>
    </div>
</template>

<script>
import PDFViewer from 'pdf-viewer-vue';
import Question from './utils/questions.vue';
import Loader from '@/components/Loader/loader.vue';
import api from '@/utils/portal/lesson.js';
import apiQuiz from '@/utils/portal/quiz';
import apiCourse from '@/utils/portal/course.js';
import playerVideo from '@/utils/storage/playerVideo';
import { toast } from 'vue3-toastify';

import { mapState, mapActions } from 'vuex';

export default {
    components: {
        PDFViewer,
        Question,
        Loader,
    },
    emits: ['progress-updated'],
    props: {
        modulesData: {
            type: Array,
            default: null,
        },
        courseName: {
            type: String,
            default: '',
        },
        modulesLoading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            data: null,
            locallyViewedLessonIds: {},
            lessonRequestSeq: 0,
            dataQuiz: [],
            pdfSource: null,
            answers: [],
            review: false,
            hasReadyQuiz: false,
            hasReadyQuizScore: false,
            correctAnswerIndices: [],
            idQuiz: null,
            scoreData: [
                {
                    acertos: 0,
                    erros: 0,
                },
            ],
            videoLink: null,
            typeVideo: null,
            hasReady: false,
            heightPlayer: 500,
            videoPlayer: null,
            playerError: false,
            playerLoading: false,
            playerLoadTimer: null,
            isMobile: false,
            returnAnswer: null,
            userAmswer: [],
            lessonLoading: false,
            lessonError: false,
            enrollmentDenied: false,
            completing: false,
            showCourseFinished: false,
        };
    },
    computed: {
        ...mapState([
            'token',
            'currentModuleOpen',
            'idLessonPortal',
            'nameCurrentLesson',
            'typeLessonPortal',
            'currentLesson',
            'currentCoursePortal',
            'porcetageProgressCurrentCourse',
            'dark',
        ]),
        hasNoLessons() {
            if (this.modulesLoading) return false;
            if (!Array.isArray(this.modulesData)) return false;
            return this.totalLessons === 0;
        },
        lessonDetail() {
            const d = this.data;
            if (!d || typeof d !== 'object' || Array.isArray(d)) {
                return null;
            }
            return d;
        },
        showLessonAbout() {
            if (Number(this.typeLessonPortal) === 5) {
                return false;
            }
            return this.lessonDetail != null && !this.lessonLoading && !this.lessonError;
        },
        showLessonNav() {
            if (this.lessonLoading || this.lessonError || this.showCourseFinished) {
                return false;
            }
            if (this.totalLessons === 0) return false;
            return this.lessonDetail != null || Number(this.typeLessonPortal) === 5;
        },
        showLessonDetails() {
            return this.showLessonAbout || this.showLessonNav;
        },
        lessonDescriptionPlain() {
            const raw = this.lessonDetail?.description;
            if (raw == null || String(raw).trim() === '') {
                return '';
            }
            return String(raw)
                .replace(/<[^>]*>/g, ' ')
                .replace(/\r\n/g, '\n')
                .replace(/[ \t]+\n/g, '\n')
                .replace(/\n{3,}/g, '\n\n')
                .trim();
        },
        /** Flag 0/1 da API — não tratar como percentual assistido. */
        isLessonCompleted() {
            const p = this.lessonDetail?.progress;
            if (p == null) {
                // Fallback: flag na lista de módulos
                const entry = this.currentLessonEntry;
                return Number(entry?.lesson?.progress) === 1;
            }
            return Number(p) === 1;
        },
        /** Percentual real da aula só quando a API envia valor fora da escala de flag (0/1). */
        lessonProgressPercent() {
            const p = this.lessonDetail?.progress;
            if (p == null) return null;
            const n = Number(p);
            if (Number.isNaN(n)) return null;
            if (n === 0 || n === 1) return null;
            if (n > 0 && n < 1) {
                return Math.min(100, Math.max(0, Math.round(n * 100)));
            }
            return Math.min(100, Math.max(0, Math.round(n)));
        },
        courseProgressRounded() {
            const p = this.lessonDetail?.progressCourse;
            if (p == null) return null;
            const n = Number(p);
            if (Number.isNaN(n)) return null;
            return Math.min(100, Math.max(0, Math.round(n)));
        },
        formattedVideoDuration() {
            const raw = this.lessonDetail?.videoTime;
            if (raw == null || raw === '') return '';
            return this.formatSecondsDuration(raw);
        },
        flattenedLessonEntries() {
            if (!this.modulesData || !Array.isArray(this.modulesData)) {
                return [];
            }
            const out = [];
            this.modulesData.forEach((mod, moduleIndex) => {
                if (!mod || !Array.isArray(mod.lessons)) return;
                mod.lessons.forEach((lesson, lessonIndex) => {
                    if (lesson && lesson.id != null) {
                        out.push({ lesson, moduleIndex, lessonIndex, module: mod });
                    }
                });
            });
            return out;
        },
        totalLessons() {
            return this.flattenedLessonEntries.length;
        },
        currentLessonIndex() {
            const id = this.idLessonPortal;
            if (id == null) return -1;
            return this.flattenedLessonEntries.findIndex(
                (e) => String(e.lesson.id) === String(id),
            );
        },
        currentLessonEntry() {
            const idx = this.currentLessonIndex;
            if (idx < 0) return null;
            return this.flattenedLessonEntries[idx];
        },
        currentModuleTitle() {
            return this.currentLessonEntry?.module?.title || '';
        },
        positionLabel() {
            if (this.totalLessons <= 0 || this.currentLessonIndex < 0) return '';
            return this.$t('student.header.lessonOfTotal', {
                n: this.currentLessonIndex + 1,
                total: this.totalLessons,
            });
        },
        lessonTypeLabel() {
            void this.$i18n.locale;
            const t = Number(this.typeLessonPortal ?? this.lessonDetail?.type);
            if (t === 1) return this.$t('student.player.lessonTypeVideoLibrary');
            if (t === 2 || t === 3) return this.$t('student.player.lessonTypeVideo');
            if (t === 4) return this.$t('student.player.lessonTypePdf');
            if (t === 5) return this.$t('student.player.lessonTypeQuiz');
            return '';
        },
        /** Partes da linha de metadados: Módulo • Aula N de M • Tipo */
        lessonMetaParts() {
            const parts = [];
            if (this.currentModuleTitle) {
                parts.push(this.currentModuleTitle);
            }
            if (this.positionLabel) {
                parts.push(this.positionLabel);
            } else if (this.currentLessonEntry) {
                parts.push(this.$t('student.header.lessonN', { n: this.currentLessonEntry.lessonIndex + 1 }));
            } else if (this.lessonDetail?.order != null) {
                parts.push(this.$t('student.header.lessonN', { n: this.lessonDetail.order }));
            }
            if (this.lessonTypeLabel) {
                parts.push(this.lessonTypeLabel);
            }
            return parts;
        },
        lessonMetaLine() {
            return this.lessonMetaParts.join(' • ');
        },
        nextLessonEntry() {
            const idx = this.currentLessonIndex;
            const flat = this.flattenedLessonEntries;
            if (idx === -1 || idx >= flat.length - 1) return null;
            return flat[idx + 1];
        },
        previousLessonEntry() {
            const idx = this.currentLessonIndex;
            if (idx <= 0) return null;
            return this.flattenedLessonEntries[idx - 1];
        },
        hasNextLesson() {
            return this.nextLessonEntry != null;
        },
        hasPreviousLesson() {
            return this.previousLessonEntry != null;
        },
        nextLessonTitle() {
            return this.nextLessonEntry?.lesson?.title || '';
        },
        previousLessonTitle() {
            return this.previousLessonEntry?.lesson?.title || '';
        },
        nextLessonAriaLabel() {
            const t = this.nextLessonTitle;
            return t
                ? this.$t('student.player.nextLessonAria', { title: t })
                : this.$t('student.player.nextLessonAriaGeneric');
        },
        previousLessonAriaLabel() {
            const t = this.previousLessonTitle;
            return t
                ? this.$t('student.player.prevLessonAria', { title: t })
                : this.$t('student.player.prevLessonAriaGeneric');
        },
        /** A conclusão é gravada ao abrir a atividade. O botão só navega. */
        showCompleteAction() {
            const t = Number(this.typeLessonPortal);
            if (t === 5) return false;
            if (!this.lessonDetail) return false;
            return !this.isLessonCompleted;
        },
        primaryActionLabel() {
            void this.$i18n.locale;
            if (!this.hasNextLesson) {
                return this.$t('student.player.completeCourse');
            }
            const nextMod = this.nextLessonEntry?.moduleIndex;
            const curMod = this.currentLessonEntry?.moduleIndex;
            if (
                nextMod != null
                && curMod != null
                && nextMod !== curMod
            ) {
                return this.$t('student.player.completeModule');
            }
            return this.$t('student.player.completeAndContinue');
        },
    },
    methods: {
        ...mapActions([
            'storePorcetageProgressCurrentCourse',
            'storeIdLessonPortal',
            'storeCurrentLesson',
            'storeTypeLessonPortal',
            'storeCurrentModuleOpen',
            'storeNameCurrentLesson',
            'storeShowSidebar',
        ]),
        async getPlayerVideo() {
            const id = this.currentLesson?.id ?? this.idLessonPortal;
            this.clearPlayerLoadTimer();
            this.playerError = false;
            this.playerLoading = true;
            this.videoPlayer = null;
            this.hasReady = false;

            if (!id || !this.token) {
                this.playerError = true;
                this.playerLoading = false;
                this.hasReady = true;
                return;
            }

            try {
                const url = await playerVideo.getPlayerVideo(this.token, id);
                if (!url) {
                    this.playerError = true;
                    this.playerLoading = false;
                    this.hasReady = true;
                    return;
                }
                this.videoPlayer = url;
                // Mantém o loader até o iframe disparar @load (ou timeout de segurança).
                this.playerLoadTimer = setTimeout(() => {
                    if (this.playerLoading) {
                        this.playerLoading = false;
                        this.hasReady = true;
                    }
                }, 20000);
            } catch (error) {
                console.error('Erro ao obter o player:', error);
                this.playerError = true;
                this.playerLoading = false;
                this.hasReady = true;
            }
        },
        onPlayerIframeLoad() {
            this.clearPlayerLoadTimer();
            this.playerLoading = false;
            this.hasReady = true;
        },
        clearPlayerLoadTimer() {
            if (this.playerLoadTimer) {
                clearTimeout(this.playerLoadTimer);
                this.playerLoadTimer = null;
            }
        },
        async getDataQuiz(idQuiz) {
            try {
                const response = await apiQuiz.getDataQuizQuestion(this.token, idQuiz);
                this.dataQuiz = response;
            } catch (error) {
                console.error('Erro ao carregar o quiz:', error);
                if (Number(error?.response?.status) === 403) {
                    this.enrollmentDenied = true;
                    this.lessonError = true;
                }
            }
        },
        async getCourseProgress() {
            try {
                if (
                    this.currentCoursePortal
                    && this.currentCoursePortal.course
                    && this.currentCoursePortal.course.id
                ) {
                    const courseResponse = await apiCourse.getDataCourse(
                        this.token,
                        this.currentCoursePortal.course.id,
                    );
                    if (courseResponse && courseResponse.progress != null) {
                        const next = Number(courseResponse.progress);
                        const current = Number(this.porcetageProgressCurrentCourse);
                        if (Number.isNaN(next)) return;
                        if (!Number.isNaN(current) && next < current) return;
                        this.storePorcetageProgressCurrentCourse({
                            porcetageProgressCurrentCourse: next,
                        });
                    }
                }
            } catch (error) {
                console.error('Erro ao carregar progresso do curso:', error);
                if (Number(error?.response?.status) === 403) {
                    this.enrollmentDenied = true;
                }
            }
        },
        async getDataLesson() {
            if (this.hasNoLessons) {
                this.lessonLoading = false;
                this.lessonError = false;
                this.data = null;
                return;
            }
            if (!this.currentCoursePortal?.course?.id || this.idLessonPortal == null) {
                return;
            }
            const seq = ++this.lessonRequestSeq;
            const requestedId = this.idLessonPortal;
            this.lessonLoading = true;
            this.lessonError = false;
            this.enrollmentDenied = false;
            this.showCourseFinished = false;
            try {
                const response = await api.getDataLesson(
                    this.token,
                    this.currentCoursePortal.course.id,
                    requestedId,
                );
                if (seq !== this.lessonRequestSeq) return;

                const viewedLocally = Boolean(this.locallyViewedLessonIds[String(requestedId)]);
                const lesson = viewedLocally && Number(response?.progress) !== 1
                    ? { ...response, progress: 1 }
                    : response;

                this.data = lesson;
                this.applyQuizReviewState(lesson);

                switch (Number(lesson.type)) {
                    case 4:
                        this.pdfSource = lesson.finalPath || null;
                        this.hasReady = Boolean(lesson.finalPath);
                        break;
                    case 5:
                        if (lesson.idQuiz) {
                            this.idQuiz = lesson.idQuiz;
                            this.hasReadyQuizScore =
                                lesson.listUserAnswer && lesson.listUserAnswer.length > 0;
                            this.answersQuiz = lesson.listUserAnswer || [];
                            this.userAnswersRaw = lesson.listUserAnswer || [];
                            this.getDataQuiz(lesson.idQuiz);
                            this.userAmswer = lesson.listUserAnswer;
                            if (this.hasReadyQuizScore) {
                                this.scoreData = this.scoreFromAnswers(lesson.listUserAnswer);
                            }
                        }
                        break;
                    case 2:
                        this.typeVideo = 'youtube';
                        this.videoLink = this.getEmbeddedLink(lesson.path);
                        this.hasReady = Boolean(this.videoLink);
                        break;
                    case 3:
                        this.typeVideo = 'vimeo';
                        this.videoLink = this.getVimeoVideoId(lesson.path);
                        this.hasReady = Boolean(this.videoLink);
                        break;
                    case 1:
                        this.playerLoading = true;
                        this.getPlayerVideo();
                        break;
                    default:
                        console.warn('Tipo de lição desconhecido:', lesson.type);
                }

                await this.markViewedIfNeeded(lesson);
            } catch (error) {
                if (seq !== this.lessonRequestSeq) return;
                console.error('Erro ao carregar a lesson:', error);
                this.data = null;
                this.enrollmentDenied = Number(error?.response?.status) === 403;
                this.lessonError = !this.hasNoLessons;
            } finally {
                if (seq === this.lessonRequestSeq) {
                    this.lessonLoading = false;
                }
            }
        },
        retryLoadLesson() {
            this.getDataLesson();
        },
        storeAnswer(answer, questionIndex) {
            this.answers[questionIndex] = answer;
        },
        getEmbeddedLink(link) {
            if (!link || typeof link !== 'string') return null;
            if (link.includes('youtu.be')) {
                return link.split('/').pop().split('?')[0];
            } else if (link.includes('youtube.com/watch')) {
                const params = new URLSearchParams(new URL(link).search);
                return params.get('v');
            } else if (link.includes('youtube.com/embed/')) {
                return link.split('/embed/')[1].split('?')[0];
            } else {
                alert(this.$t('student.player.youtubeInvalidLink'));
                return null;
            }
        },
        getVimeoVideoId(link) {
            if (!link || typeof link !== 'string') return null;
            const videoIdMatch = link.match(/vimeo\.com\/(\d+)/);
            return videoIdMatch ? videoIdMatch[1] : null;
        },
        detectMobile() {
            this.isMobile =
                /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                    navigator.userAgent,
                );
        },
        quizMinimum(lesson) {
            const raw = lesson?.minimum ?? lesson?.minGrade ?? lesson?.quiz?.minimum;
            if (raw == null || raw === '') return 0;
            const n = Number(raw);
            return Number.isNaN(n) ? 0 : n;
        },
        quizRequiresMinimum(lesson) {
            return Number(lesson?.type) === 5 && this.quizMinimum(lesson) > 0;
        },
        scoreFromAnswers(answers) {
            const list = Array.isArray(answers) ? answers : [];
            const acertos = list.filter((item) => item.correct === 1).length;
            const erros = list.filter((item) => item.correct === 0).length;
            const total = acertos + erros;
            return {
                acertos,
                erros,
                grade: total ? (acertos / total) * 100 : 0,
            };
        },
        meetsQuizMinimum(lesson, answers) {
            const minimum = this.quizMinimum(lesson);
            if (minimum <= 0) return true;
            const { grade } = this.scoreFromAnswers(answers);
            return grade >= minimum * 10;
        },
        applyQuizReviewState(lesson) {
            if (Number(lesson?.type) !== 5) return;
            const answers = lesson?.listUserAnswer || [];
            if (!answers.length) {
                this.review = false;
                return;
            }
            const passed = !this.quizRequiresMinimum(lesson)
                || Number(lesson.progress) === 1
                || this.meetsQuizMinimum(lesson, answers);
            this.review = passed;
        },
        rememberViewedLesson(lessonId) {
            if (lessonId == null) return;
            const key = String(lessonId);
            if (this.locallyViewedLessonIds[key]) return;
            this.locallyViewedLessonIds = {
                ...this.locallyViewedLessonIds,
                [key]: true,
            };
        },
        async markViewedIfNeeded(lesson) {
            const lessonId = this.idLessonPortal;
            if (!lesson || lessonId == null) return;

            if (Number(lesson.type) === 5) {
                if (Number(lesson.progress) === 1) {
                    this.rememberViewedLesson(lessonId);
                    this.$emit('progress-updated', lessonId);
                }
                return;
            }

            if (Number(lesson.progress) !== 1) return;

            this.rememberViewedLesson(lessonId);
            if (Number(this.data?.progress) !== 1) {
                this.data = { ...(this.data || lesson), progress: 1 };
            }
            this.$emit('progress-updated', lessonId);
        },
        async handleQuizSubmitted() {
            await this.getDataLesson();
            const lesson = this.data;
            if (!lesson || Number(lesson.type) !== 5) return;
            if (Number(lesson.progress) === 1) {
                this.rememberViewedLesson(this.idLessonPortal);
                this.review = true;
                this.$emit('progress-updated', this.idLessonPortal);
                return;
            }
            this.review = false;
        },
        formatSecondsDuration(value) {
            const sec = Number(value);
            if (Number.isNaN(sec) || sec < 0) return '';
            if (sec === 0) return this.$t('student.courseCard.durationZero');
            const h = Math.floor(sec / 3600);
            const m = Math.floor((sec % 3600) / 60);
            const parts = [];
            if (h > 0) parts.push(this.$t('student.courseCard.durationHours', { h }));
            if (m > 0 || h === 0) parts.push(this.$t('student.courseCard.durationMinutes', { m }));
            return parts.join(' ');
        },
        applyLessonEntry(entry) {
            if (!entry) return;
            const { lesson, moduleIndex, lessonIndex } = entry;
            this.storeIdLessonPortal({ idLessonPortal: lesson.id });
            this.storeCurrentLesson({ currentLesson: lessonIndex });
            this.storeTypeLessonPortal({ typeLessonPortal: lesson.type });
            this.storeCurrentModuleOpen({ currentModuleOpen: moduleIndex });
            this.storeNameCurrentLesson({ nameCurrentLesson: lesson.title });
            this.storeCurrentLesson({ currentLesson: lesson });
            if (typeof window !== 'undefined' && window.innerWidth < 960) {
                this.storeShowSidebar({ showSidebar: false });
            }
        },
        goToNextLesson() {
            this.applyLessonEntry(this.nextLessonEntry);
        },
        goToPreviousLesson() {
            this.applyLessonEntry(this.previousLessonEntry);
        },
        goToCourses() {
            this.$router.push('/aluno/dashboard');
        },
        async completeAndContinue() {
            if (this.completing || this.isLessonCompleted) return;
            if (!this.idLessonPortal) return;

            this.completing = true;
            try {
                await api.updateProgressLesson(this.token, this.idLessonPortal);
                this.rememberViewedLesson(this.idLessonPortal);
                this.data = { ...(this.data || {}), progress: 1 };
                this.$emit('progress-updated', this.idLessonPortal);

                if (this.hasNextLesson) {
                    this.goToNextLesson();
                } else {
                    await this.getDataLesson();
                    await this.getCourseProgress();
                    this.showCourseFinished = true;
                }
            } catch (error) {
                console.error('Erro ao concluir a aula:', error);
                toast.error(this.$t('student.player.completeLessonError'));
            } finally {
                this.completing = false;
            }
        },
    },
    created() {
        this.detectMobile();
        this.getDataLesson();
    },
    beforeUnmount() {
        this.clearPlayerLoadTimer();
    },
    watch: {
        modulesData: {
            deep: true,
            handler() {
                const id = this.idLessonPortal;
                if (id == null || !this.data) return;
                const entry = this.flattenedLessonEntries.find(
                    (item) => String(item.lesson.id) === String(id),
                );
                if (Number(entry?.lesson?.progress) === 1 && Number(this.data.progress) !== 1) {
                    this.data = { ...this.data, progress: 1 };
                }
            },
        },
        idLessonPortal() {
            this.hasReady = false;
            this.review = false;
            this.pdfSource = null;
            this.videoLink = null;
            this.videoPlayer = null;
            this.playerError = false;
            this.playerLoading = Number(this.typeLessonPortal) === 1;
            this.clearPlayerLoadTimer();
            this.getDataLesson();
        },
        typeLessonPortal() {
            this.videoLink = null;
            this.typeVideo = null;
            this.playerError = false;
            this.playerLoading = Number(this.typeLessonPortal) === 1;
            this.clearPlayerLoadTimer();
        },
        currentCoursePortal(newValue, oldValue) {
            if (oldValue && newValue && oldValue.course?.id !== newValue.course?.id) {
                this.review = false;
                this.hasReady = false;
                this.hasReadyQuizScore = false;
                this.userAmswer = [];
                this.dataQuiz = [];
                this.scoreData = [
                    {
                        acertos: 0,
                        erros: 0,
                    },
                ];
            }
        },
    },
};
</script>

<style scoped>
.lesson-view {
    --lv-page: #F5F7FA;
    --lv-bg: #FFFFFF;
    --lv-border: #E4E7EC;
    --lv-text: #1F2937;
    --lv-muted: #667085;
    --lv-success: #039855;
    --lv-error: #D92D20;
    --lv-accent: #1ac4cd;

    flex: 1;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: auto;
    background: var(--lv-page);
}

.lesson-view--dark {
    --lv-page: #0f0e12;
    --lv-bg: #14131a;
    --lv-border: #2a3040;
    --lv-text: #f2f4f8;
    --lv-muted: #9aa3b2;
}

.lesson-view__inner {
    flex: 1 1 auto;
    width: 100%;
    max-width: 1200px;
    min-width: 0;
    margin: 0 auto;
    padding: 1rem 1.25rem 2rem;
    box-sizing: border-box;
}

/* Context header */
.lesson-context {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem 1rem;
    margin-bottom: 0.85rem;
}

.lesson-context__breadcrumb {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    min-width: 0;
    flex: 1;
    font-size: 0.8125rem;
    color: var(--lv-muted);
}

.lesson-context__crumb {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 280px;
}

.lesson-context__crumb--current {
    color: var(--lv-text);
    font-weight: 600;
}

.lesson-context__sep {
    flex-shrink: 0;
    opacity: 0.7;
}

.lesson-context__position {
    margin: 0;
    flex-shrink: 0;
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--lv-muted);
    font-variant-numeric: tabular-nums;
}

/* Loading / states */
.lesson-loading__player {
    width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: 10px;
    overflow: hidden;
    background: #0a0a0b;
    margin-bottom: 1rem;
}

.lesson-loading__player :deep(.v-skeleton-loader__image) {
    height: 100%;
}

.lesson-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.65rem;
    min-height: 280px;
    padding: 2rem 1.25rem;
    text-align: center;
    background: var(--lv-bg);
    border: 1px solid var(--lv-border);
    border-radius: 10px;
}

.lesson-state__title {
    margin: 0;
    font-size: 1.0625rem;
    font-weight: 600;
    color: var(--lv-text);
}

.lesson-state__text {
    margin: 0 0 0.5rem;
    max-width: 28rem;
    font-size: 0.875rem;
    color: var(--lv-muted);
}

/* Media */
.lesson-media {
    width: 100%;
    max-width: 1100px;
    margin: 0 auto 0.75rem;
}

.lesson-media--in-details {
    max-width: none;
    margin: 1.15rem 0 0;
}

.lesson-media__aspect {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    max-height: min(70vh, 620px);
    background: #0a0a0b;
    border-radius: 10px;
    overflow: hidden;
}

.lesson-media__aspect iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
}

.lesson-media__iframe {
    opacity: 0;
    transition: opacity 0.2s ease;
}

.lesson-media__iframe--ready {
    opacity: 1;
}

.lesson-media__loader {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.85rem;
    background: #0a0a0b;
    color: #F2F4F7;
}

.lesson-media__loader-text {
    font-size: 0.875rem;
    font-weight: 500;
    color: #D0D5DD;
}

/* PDF */
.lesson-pdf {
    width: 100%;
    max-width: 1100px;
    margin: 0 auto 0.75rem;
    min-height: min(70vh, 640px);
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid var(--lv-border);
    background: var(--lv-bg);
}

.lesson-pdf__frame,
.lesson-pdf__embed {
    display: block;
    width: 100%;
    height: min(70vh, 640px);
    border: none;
}

.lesson-pdf__empty {
    padding: 2rem;
    text-align: center;
    color: var(--lv-muted);
    font-size: 0.875rem;
}

/* Detalhes da aula (info + navegação) */
.lesson-details {
    width: 100%;
    max-width: 1100px;
    margin: 1.15rem auto 0;
    background: #FFFFFF;
    border: 1px solid #E4E7EC;
    border-radius: 10px;
    padding: 1.25rem 1.5rem;
    box-sizing: border-box;
    text-align: left;
}

.lesson-view--dark .lesson-details {
    background: var(--lv-bg);
    border-color: var(--lv-border);
}

.lesson-details__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem 1.25rem;
}

.lesson-details__main {
    flex: 1;
    min-width: 0;
}

.lesson-details__meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.35rem;
    margin: 0;
    font-size: 0.8125rem;
    font-weight: 500;
    line-height: 1.4;
    color: #667085;
}

.lesson-view--dark .lesson-details__meta {
    color: var(--lv-muted);
}

.lesson-details__meta-sep {
    color: #98A2B3;
}

.lesson-details__title {
    margin: 0.55rem 0 0.4rem;
    font-size: 1.375rem;
    font-weight: 700;
    line-height: 1.3;
    color: #1F2937;
    text-align: left;
    word-break: break-word;
}

.lesson-view--dark .lesson-details__title {
    color: var(--lv-text);
}

.lesson-details__description {
    margin: 0;
    max-width: 800px;
    font-size: 0.9375rem;
    line-height: 1.55;
    color: #667085;
    text-align: left;
    white-space: pre-line;
}

.lesson-view--dark .lesson-details__description {
    color: var(--lv-muted);
}

.lesson-details__status {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.3rem 0.7rem;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1.2;
    color: #039855;
    background: #ECFDF3;
    white-space: nowrap;
}

.lesson-view--dark .lesson-details__status {
    background: #1c3228;
}

.lesson-details__navigation {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-top: 1.35rem;
    padding-top: 1.15rem;
    border-top: 1px solid #E4E7EC;
}

.lesson-view--dark .lesson-details__navigation {
    border-top-color: var(--lv-border);
}

.lesson-details__navigation--alone {
    margin-top: 0;
    padding-top: 0;
    border-top: none;
}

.lesson-details__previous,
.lesson-details__next {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    min-width: 0;
    max-width: 48%;
}

.lesson-details__next {
    align-items: flex-end;
    margin-left: auto;
    text-align: right;
}

.lesson-details__btn {
    min-height: 40px;
    text-transform: none !important;
    letter-spacing: 0 !important;
    font-weight: 600 !important;
    box-shadow: none !important;
}

.lesson-details__btn--prev {
    min-height: 40px;
}

.lesson-details__hint {
    max-width: 260px;
    font-size: 0.75rem;
    line-height: 1.35;
    color: #667085;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    word-break: break-word;
}

.lesson-view--dark .lesson-details__hint {
    color: var(--lv-muted);
}

.lesson-details__next .lesson-details__hint {
    text-align: right;
}

.lesson-view :deep(.v-btn:focus-visible) {
    outline: 2px solid var(--lv-accent);
    outline-offset: 2px;
}

/* Quiz */
.lesson-quiz {
    width: 100%;
    margin: 0 auto;
}

.quiz-content {
    padding: 0;
    margin-top: 4px;
}

.lesson-quiz__summary {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    width: 100%;
    margin: 0 auto 24px;
}

.lesson-quiz__stat {
    padding: 16px 12px;
    border-radius: 10px;
    text-align: center;
    border: 1px solid var(--lv-border);
    background: var(--lv-bg);
}

.lesson-quiz__stat-label {
    margin: 0 0 8px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--lv-muted);
}

.lesson-quiz__stat-value {
    margin: 0;
    font-size: 24px;
    font-weight: 700;
    line-height: 1.2;
    color: var(--lv-text);
    font-variant-numeric: tabular-nums;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.lesson-quiz__stat-value--xl {
    font-size: 32px;
    font-weight: 800;
}

.lesson-quiz__stat-icon {
    font-size: 1.05rem;
}

.lesson-quiz__stat--grade {
    background: rgba(26, 196, 205, 0.07);
    border-color: rgba(26, 196, 205, 0.25);
}

.lesson-quiz__stat--grade .lesson-quiz__stat-value {
    color: #0f8f96;
}

.lesson-quiz__stat--hits {
    background: rgba(3, 152, 85, 0.07);
    border-color: rgba(3, 152, 85, 0.22);
}

.lesson-quiz__stat--hits .lesson-quiz__stat-icon,
.lesson-quiz__stat--hits .lesson-quiz__stat-value {
    color: var(--lv-success);
}

.lesson-quiz__stat--errors {
    background: rgba(217, 45, 32, 0.07);
    border-color: rgba(217, 45, 32, 0.22);
}

.lesson-quiz__stat--errors .lesson-quiz__stat-icon,
.lesson-quiz__stat--errors .lesson-quiz__stat-value {
    color: var(--lv-error);
}

.lesson-quiz :deep(.box_question_quiz) {
    border-radius: 10px;
    border: 1px solid var(--lv-border);
    box-shadow: none;
}

.lesson-view--dark .lesson-quiz :deep(.box_question_quiz) {
    background-color: var(--lv-bg);
    color: var(--lv-text);
    border-color: var(--lv-border);
}

.lesson-view--dark .lesson-quiz :deep(.number_question_quiz) {
    color: #94a3b8;
    border-bottom: 1px solid var(--lv-border);
}

.lesson-view--dark .lesson-quiz :deep(.title_question_quiz),
.lesson-view--dark .lesson-quiz :deep(.title_question_quiz p),
.lesson-view--dark .lesson-quiz :deep(.title_question_quiz span) {
    color: var(--lv-text);
}

.lesson-view--dark .lesson-quiz :deep(.option_question_quiz:hover) {
    background-color: rgba(255, 255, 255, 0.05);
}

.lesson-view--dark .lesson-quiz :deep(span.option_input) {
    background-color: #1e293b;
    border-color: #64748b;
    color: #f1f5f9;
}

.lesson-view--dark .lesson-quiz :deep(span.selected.option_input) {
    background-color: #0d9488;
    border-color: #5eead4;
    color: #ffffff;
}

.lesson-view--dark .lesson-quiz :deep(.box_question_quiz.review span.correct.option_input) {
    background-color: #166534;
    border-color: #4ade80;
    color: #ffffff;
}

.lesson-view--dark .lesson-quiz :deep(span.selected.option_input.incorrect),
.lesson-view--dark .lesson-quiz :deep(span.option_input.incorrect) {
    background-color: #991b1b;
    border-color: #f87171;
    color: #ffffff;
}

.lesson-view--dark .lesson-quiz :deep(.quiz-resolution) {
    background: rgba(26, 196, 205, 0.1);
    border-color: rgba(26, 196, 205, 0.35);
}

.lesson-view--dark .lesson-quiz :deep(.quiz-resolution__label) {
    color: #5eead4;
}

.lesson-view--dark .lesson-quiz :deep(.quiz-resolution__text) {
    color: var(--lv-text);
}

.lesson-view--dark .lesson-quiz :deep(.enviar-quiz-container) {
    border-top: 1px solid var(--lv-border);
}

@media (max-width: 959px) {
    .lesson-view__inner {
        padding: 12px 16px 24px;
    }

    /* Contexto do curso migrou para o header mobile */
    .lesson-context {
        display: none;
    }

    .lesson-details {
        padding: 16px;
    }

    .lesson-details__header {
        flex-direction: column;
        gap: 0.65rem;
    }

    .lesson-details__status {
        align-self: flex-start;
        order: -1;
    }

    .lesson-details__navigation {
        flex-direction: column-reverse;
        gap: 0.75rem;
    }

    .lesson-details__previous,
    .lesson-details__next {
        max-width: none;
        width: 100%;
        align-items: stretch;
    }

    .lesson-details__next {
        margin-left: 0;
        text-align: left;
    }

    .lesson-details__btn {
        width: 100%;
    }

    .lesson-details__hint {
        max-width: none;
    }

    .lesson-details__next .lesson-details__hint {
        text-align: left;
    }
}

@media (max-width: 768px) {
    .lesson-quiz__summary {
        grid-template-columns: 1fr;
    }

    .lesson-media__aspect {
        max-height: none;
    }

    .lesson-pdf__frame,
    .lesson-pdf__embed {
        height: 60vh;
    }
}

@media (max-width: 480px) {
    .lesson-view__inner {
        padding: 12px 12px 24px;
    }

    .lesson-details__title {
        font-size: 1.15rem;
    }

    .lesson-details__btn {
        min-height: 44px;
    }

    .lesson-pdf__frame,
    .lesson-pdf__embed {
        height: 50vh;
    }
}

@media (min-width: 1600px) {
    .lesson-media,
    .lesson-details {
        max-width: 1040px;
    }
}
</style>
